import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HiOutlineChevronDown, HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi2';
import { deriveAvatarInitial } from '../../components/AvatarEditPopup';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import { createProjectFromCommunityTemplate } from '../../services/projectService';
import SafeAvatar from '../../components/SafeAvatar';

const FALLBACK_THUMB = 'https://placehold.co/640x400/f1f5f9/94a3b8?text=Template';

/** 로컬 기준 오늘 → YYYY-MM-DD */
function todayLocalISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * 카테고리별 가로 스크롤 행 — 화면 밖에 카드가 더 있으면 좌/우 chevron 버튼을 띄워
 * 좌우 스크롤 가능함을 알리고, 클릭 시 카드 1개씩 이동.
 */
function CatRowScroll({ children }) {
  const { t } = useTranslation('community');
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const childCount = Array.isArray(children) ? children.length : 0;

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    // 시작 근처(스냅·반올림으로 0 이 아닌 미세값이 남는 경우 포함)에서는 왼쪽 버튼 숨김
    const EDGE = 8;
    setCanLeft(el.scrollLeft > EDGE);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - EDGE);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  // 카드 수가 바뀌거나(로딩) 레이아웃이 잡힌 뒤 다시 계산
  useEffect(() => {
    const raf = requestAnimationFrame(update);
    return () => cancelAnimationFrame(raf);
  }, [update, childCount]);

  const step = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('.community-card');
    const styles = getComputedStyle(el);
    const gap = parseFloat(styles.columnGap || styles.gap) || 14;
    const cardW = card ? card.getBoundingClientRect().width : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * (cardW + gap), behavior: 'smooth' });
  };

  return (
    <div className="community-cat-row-scroll-wrap">
      {canLeft && (
        <button
          type="button"
          className="community-cat-row-nav community-cat-row-nav--left"
          onClick={() => step(-1)}
          aria-label={t('common.prevCard')}
        >
          <HiOutlineChevronLeft aria-hidden />
        </button>
      )}
      <div className="community-cat-row-scroll" role="list" ref={scrollRef}>
        {children}
      </div>
      {canRight && (
        <button
          type="button"
          className="community-cat-row-nav community-cat-row-nav--right"
          onClick={() => step(1)}
          aria-label={t('common.nextCard')}
        >
          <HiOutlineChevronRight aria-hidden />
        </button>
      )}
    </div>
  );
}

/** tasks 전역 최소 start·최대 end(타임스탬프 ms)로 캘린더 일 수(양끝 포함) */
function spanDaysInclusive(minStartMs, maxEndMs) {
  if (minStartMs == null || maxEndMs == null) return null;
  const a = new Date(minStartMs);
  const b = new Date(maxEndMs);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  const diff = Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
  return diff > 0 ? diff : null;
}

/** ISO 문자열 → YYYY.MM.DD */
function formatTemplateUpdatedDate(isoDate) {
  if (!isoDate) return '—';
  const d = new Date(isoDate);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  }
  const datePart = String(isoDate).split('T')[0];
  const parts = datePart.split('-');
  if (parts.length >= 3) return `${parts[0]}.${parts[1]}.${parts[2]}`;
  return String(isoDate);
}

/** 인기순이 목록·기본값에서 먼저 오도록 순서 고정 */
const TEMPLATE_SORT_OPTIONS = [{ id: 'popular' }, { id: 'recent' }];

function TemplatesTab({ onRequireLogin }) {
  const { t } = useTranslation('community');
  // 카테고리명은 DB에 한글로 저장되고 필터·그룹핑 키로도 쓰이므로, 값은 그대로 두고
  // 화면에 보일 때만 번역 맵으로 치환한다. 맵에 없는 카테고리는 원문(한글)으로 폴백.
  const catLabel = (name) => {
    if (!name) return name;
    const map = t('categories', { returnObjects: true });
    return (map && map[name]) || name;
  };
  const navigate = useNavigate();
  const { user } = useAuth();
  const [templateSearchQuery, setTemplateSearchQuery] = useState('');
  const [templateCards, setTemplateCards] = useState([]);
  const [templateListLoading, setTemplateListLoading] = useState(true);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [templateCategories, setTemplateCategories] = useState([]);
  const [templateCategoriesLoading, setTemplateCategoriesLoading] = useState(true);
  const [selectedCategoryName, setSelectedCategoryName] = useState(null);
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [templateSort, setTemplateSort] = useState('popular');
  const categoryFilterRef = useRef(null);
  const sortFilterRef = useRef(null);
  /** null이면 닫힘 — 템플릿 설명 미리보기 후 대시보드 이동 */
  const [templateDescPreview, setTemplateDescPreview] = useState(null);
  /** 모달: 템플릿 적용 시작일 (기본 오늘) */
  const [templateApplyStartDate, setTemplateApplyStartDate] = useState(() => todayLocalISO());
  /** 사용하기 — DB 복사 중 */
  const [templateImportLoading, setTemplateImportLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTemplateListLoading(true);
      const { data: projects, error: pErr } = await supabase
        .from('projects')
        .select('id, name, owner_id, template_category_id, created_at, updated_at, template_desc')
        .eq('is_template', true);

      if (cancelled) return;
      if (pErr) {
        console.error('[Community] 템플릿 프로젝트 목록 실패:', pErr);
        setTemplateCards([]);
        setTemplateListLoading(false);
        return;
      }

      const list = Array.isArray(projects) ? projects : [];
      if (list.length === 0) {
        setTemplateCards([]);
        setTemplateListLoading(false);
        return;
      }

      const projectIds = list.map((p) => p.id);
      const ownerIds = [...new Set(list.map((p) => p.owner_id).filter(Boolean))];
      const templateCatIds = [...new Set(list.map((p) => p.template_category_id).filter(Boolean))];

      const [
        { data: profiles, error: profErr },
        { data: templateCats, error: tcErr },
        { data: cats, error: catErr },
        { data: usageRows, error: usageErr },
      ] = await Promise.all([
        ownerIds.length
          ? supabase
              .from('user_profiles')
              .select('id, updated_display_name, updated_avatar_url')
              .in('id', ownerIds)
          : Promise.resolve({ data: [], error: null }),
        templateCatIds.length
          ? supabase
              .from('template_categories')
              .select('id, name, image_url')
              .in('id', templateCatIds)
          : Promise.resolve({ data: [], error: null }),
        supabase
          .from('categories')
          .select('id, project_id, created_at, updated_at')
          .in('project_id', projectIds),
        supabase.from('project_usage').select('project_id, user_id').in('project_id', projectIds),
      ]);

      if (cancelled) return;
      if (profErr) console.error('[Community] user_profiles:', profErr);
      if (tcErr) console.error('[Community] template_categories:', tcErr);
      if (catErr) console.error('[Community] categories:', catErr);
      if (usageErr) console.error('[Community] project_usage:', usageErr);

      const profileById = Object.fromEntries((profiles || []).map((r) => [r.id, r]));
      const tcatById = Object.fromEntries((templateCats || []).map((r) => [r.id, r]));

      const distinctUsersByProject = {};
      for (const pid of projectIds) distinctUsersByProject[pid] = new Set();
      for (const row of usageRows || []) {
        if (row?.project_id && row?.user_id && distinctUsersByProject[row.project_id]) {
          distinctUsersByProject[row.project_id].add(row.user_id);
        }
      }

      const categoriesList = cats || [];
      const categoryIds = categoriesList.map((c) => c.id);
      const catById = Object.fromEntries(categoriesList.map((c) => [c.id, c]));

      let tasks = [];
      let checklists = [];
      if (categoryIds.length > 0) {
        const { data: tasksData, error: tasksErr } = await supabase
          .from('tasks')
          .select('id, category_id, created_at, updated_at, start_date, end_date')
          .in('category_id', categoryIds);
        if (tasksErr) console.error('[Community] tasks:', tasksErr);
        tasks = tasksData || [];
        const taskIds = tasks.map((t) => t.id);
        if (taskIds.length > 0) {
          const { data: clData, error: clErr } = await supabase
            .from('checklists')
            .select('task_id, created_at, updated_at')
            .in('task_id', taskIds);
          if (clErr) console.error('[Community] checklists:', clErr);
          checklists = clData || [];
        }
      }

      const taskById = Object.fromEntries(tasks.map((t) => [t.id, t]));

      const minTaskStartByProject = {};
      const maxTaskEndByProject = {};
      for (const pid of projectIds) {
        minTaskStartByProject[pid] = null;
        maxTaskEndByProject[pid] = null;
      }
      const bumpMinStart = (pid, iso) => {
        if (!iso || !pid) return;
        const t = new Date(iso).getTime();
        if (Number.isNaN(t)) return;
        const prev = minTaskStartByProject[pid];
        if (prev == null || t < prev) minTaskStartByProject[pid] = t;
      };
      const bumpMaxEnd = (pid, iso) => {
        if (!iso || !pid) return;
        const t = new Date(iso).getTime();
        if (Number.isNaN(t)) return;
        const prev = maxTaskEndByProject[pid];
        if (prev == null || t > prev) maxTaskEndByProject[pid] = t;
      };

      const maxDateByProject = {};
      for (const pid of projectIds) maxDateByProject[pid] = null;
      const bump = (pid, iso) => {
        if (!iso || !pid) return;
        const t = new Date(iso).getTime();
        if (Number.isNaN(t)) return;
        const prev = maxDateByProject[pid];
        if (!prev || t > new Date(prev).getTime()) maxDateByProject[pid] = iso;
      };

      for (const p of list) {
        bump(p.id, p.created_at);
        bump(p.id, p.updated_at);
      }
      for (const c of categoriesList) {
        bump(c.project_id, c.created_at);
        bump(c.project_id, c.updated_at);
      }
      for (const t of tasks) {
        const cat = catById[t.category_id];
        if (cat) {
          const pid = cat.project_id;
          bump(pid, t.created_at);
          bump(pid, t.updated_at);
          bumpMinStart(pid, t.start_date);
          bumpMaxEnd(pid, t.end_date);
        }
      }
      for (const cl of checklists) {
        const task = taskById[cl.task_id];
        if (!task) continue;
        const cat = catById[task.category_id];
        if (!cat) continue;
        bump(cat.project_id, cl.created_at);
        bump(cat.project_id, cl.updated_at);
      }

      const cards = list.map((p) => {
        const prof = p.owner_id ? profileById[p.owner_id] : null;
        const tc = p.template_category_id ? tcatById[p.template_category_id] : null;
        const latest = maxDateByProject[p.id];
        const spanDays = spanDaysInclusive(minTaskStartByProject[p.id], maxTaskEndByProject[p.id]);
        return {
          id: p.id,
          title: (p.name && String(p.name).trim()) || t('common.untitled'),
          templateDesc: (p.template_desc != null && String(p.template_desc).trim()) || '',
          category: tc?.name || '—',
          thumbImageUrl: (tc?.image_url && String(tc.image_url).trim()) || FALLBACK_THUMB,
          creatorName: prof?.updated_display_name?.trim() || t('common.creatorFallback'),
          creatorImageUrl: prof?.updated_avatar_url || '',
          // updated_avatar_url 비어 있으면 닉네임 첫 글자(한글 1글자/영문 첫 letter 대문자)
          creatorAvatarFallback: deriveAvatarInitial(prof?.updated_display_name?.trim() || ''),
          useCount: distinctUsersByProject[p.id]?.size ?? 0,
          updatedAt: latest || p.updated_at || null,
          durationDays: spanDays,
        };
      });

      setTemplateCards(cards);
      setTemplateListLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // t 포함: 언어 전환 시 카드에 박히는 fallback 라벨도 갱신
  }, [t]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('template_categories')
        .select('name')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (cancelled) return;
      setTemplateCategoriesLoading(false);
      if (error) {
        console.error('[Community] template_categories 조회 실패:', error);
        setTemplateCategories([]);
        return;
      }
      setTemplateCategories(Array.isArray(data) ? data.filter((row) => row?.name) : []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!categoryMenuOpen && !sortMenuOpen) return;
    const handlePointerDown = (e) => {
      if (categoryFilterRef.current && !categoryFilterRef.current.contains(e.target)) {
        setCategoryMenuOpen(false);
      }
      if (sortFilterRef.current && !sortFilterRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setCategoryMenuOpen(false);
        setSortMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [categoryMenuOpen, sortMenuOpen]);

  useEffect(() => {
    if (!templateDescPreview) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && !templateImportLoading) setTemplateDescPreview(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [templateDescPreview, templateImportLoading]);

  const handleUseTemplate = useCallback(
    (e, template) => {
      e.preventDefault();
      e.stopPropagation();
      if (!user) {
        onRequireLogin?.();
        return;
      }
      setTemplateApplyStartDate(todayLocalISO());
      setTemplateDescPreview({
        projectId: template.id,
        title: template.title,
        desc: template.templateDesc || '',
        durationDays: template.durationDays ?? null,
      });
    },
    [onRequireLogin, user]
  );

  const goToDashboardFromPreview = useCallback(async () => {
    if (!templateDescPreview || !user?.id || templateImportLoading) return;
    const hasSchedule = templateDescPreview.durationDays != null;
    const anchor = hasSchedule ? templateApplyStartDate : null;

    setTemplateImportLoading(true);
    try {
      const { newProjectId } = await createProjectFromCommunityTemplate({
        templateProjectId: templateDescPreview.projectId,
        userId: user.id,
        anchorDateISO: anchor,
      });
      setTemplateDescPreview(null);
      navigate('/dashboard', { state: { importedProjectId: newProjectId } });
    } catch (err) {
      console.error('[Community] 템플릿 복사:', err);
      alert(err.message || t('templates.importError'));
    } finally {
      setTemplateImportLoading(false);
    }
  }, [navigate, templateDescPreview, templateApplyStartDate, templateImportLoading, user?.id, t]);

  const filteredTemplates = useMemo(() => {
    let base = templateCards;
    if (selectedCategoryName) {
      base = base.filter((t) => t.category === selectedCategoryName);
    }
    const q = templateSearchQuery.trim().toLowerCase();
    if (q) {
      base = base.filter((t) => t.title.toLowerCase().includes(q));
    }
    const sorted = [...base];
    if (templateSort === 'popular') {
      // 인기순: project_usage 기준 사용자 수(useCount) 내림차순
      sorted.sort((a, b) => {
        const diff = b.useCount - a.useCount;
        if (diff !== 0) return diff;
        const ta = new Date(a.updatedAt || 0).getTime();
        const tb = new Date(b.updatedAt || 0).getTime();
        return tb - ta;
      });
    } else if (templateSort === 'recent') {
      // 최신순: 프로젝트·카테고리·태스크·체크리스트의 created_at/updated_at 중 최댓값(updatedAt) 내림차순
      sorted.sort((a, b) => {
        const ta = new Date(a.updatedAt || 0).getTime();
        const tb = new Date(b.updatedAt || 0).getTime();
        const diff = tb - ta;
        if (diff !== 0) return diff;
        return b.useCount - a.useCount;
      });
    }
    return sorted;
  }, [templateCards, templateSearchQuery, templateSort, selectedCategoryName]);

  // 카드 렌더 — grid·row 두 모드에서 동일 마크업 재사용
  const renderTemplateCard = (card, isRow = false) => (
    <article
      key={card.id}
      className={`community-card${isRow ? ' community-card--row' : ''}`}
      role={isRow ? 'listitem' : undefined}
    >
      <div className="community-card-thumb">
        <img
          src={card.thumbImageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          width={640}
          height={400}
        />
        <div className="community-card-thumb-overlay">
          <button
            type="button"
            className={`community-card-use-btn${user ? '' : ' community-card-use-btn--guest'}`}
            aria-label={user ? t('common.viewDesc') : t('common.viewDescGuest')}
            onClick={(e) => handleUseTemplate(e, card)}
          >
            {user ? t('common.viewDesc') : t('common.viewDescGuest')}
          </button>
        </div>
      </div>
      <div className="community-card-body">
        <h3 className="community-card-title">{card.title}</h3>
        <div className="community-card-author">
          <SafeAvatar
            className="community-card-avatar"
            fallbackClassName="community-card-avatar"
            src={card.creatorImageUrl || card.creatorAvatarFallback}
            alt=""
            imgProps={{ width: 32, height: 32, loading: 'lazy', decoding: 'async' }}
          />
          <span className="community-card-author-name">{card.creatorName}</span>
        </div>
        <div className="community-card-meta" aria-label={t('templates.statsAria')}>
          <span className="community-card-meta-line">
            {t('common.usedBefore')}
            <strong className="community-card-use-count">
              {card.useCount.toLocaleString('ko-KR')}
            </strong>
            {t('common.usedAfter')}
          </span>
          <span className="community-card-meta-line community-card-meta-line--muted">
            {t('common.lastUpdated', { date: formatTemplateUpdatedDate(card.updatedAt) })}
          </span>
        </div>
      </div>
    </article>
  );

  // 카테고리 드랍다운 옆에 보여줄 카운트 — selectedCategoryName 과 무관하게
  // 검색어만 적용한 후의 카테고리별 템플릿 수 (필터 한 번 적용 결과로 어색하지 않게)
  const categoryCounts = useMemo(() => {
    const q = templateSearchQuery.trim().toLowerCase();
    const base = q ? templateCards.filter((t) => t.title.toLowerCase().includes(q)) : templateCards;
    const counts = {};
    let total = 0;
    base.forEach((t) => {
      const k = t.category || '기타';
      counts[k] = (counts[k] || 0) + 1;
      total += 1;
    });
    counts.__all = total;
    return counts;
  }, [templateCards, templateSearchQuery]);

  // 전체 모드일 때 카테고리별 행으로 묶기 — sort 는 filteredTemplates 가 이미 적용한 순서 유지
  const groupedTemplates = useMemo(() => {
    if (selectedCategoryName) return null;
    const byCat = new Map();
    filteredTemplates.forEach((t) => {
      const cat = t.category || '기타';
      if (!byCat.has(cat)) byCat.set(cat, []);
      byCat.get(cat).push(t);
    });
    const seen = new Set();
    const groups = [];
    templateCategories.forEach((c) => {
      groups.push({ category: c.name, items: byCat.get(c.name) || [] });
      seen.add(c.name);
    });
    byCat.forEach((items, cat) => {
      if (!seen.has(cat)) groups.push({ category: cat, items });
    });
    return groups;
  }, [filteredTemplates, selectedCategoryName, templateCategories]);

  return (
    <>
      <section className="community-hero" aria-labelledby="community-hero-title">
        <div className="community-hero-inner">
          <span className="community-hero-badge">{t('templates.heroBadge')}</span>
          <h1 id="community-hero-title" className="community-hero-title">
            {t('templates.heroTitle')}
          </h1>
          <p className="community-hero-desc community-pre-line">{t('templates.heroDesc')}</p>
          <div className="community-stats">
            <div className="community-stat">
              <span className="community-stat-value">
                {templateListLoading ? (
                  <span
                    className="community-loading-dots"
                    role="status"
                    aria-label={t('common.loading')}
                  />
                ) : (
                  templateCards.length.toLocaleString('ko-KR')
                )}
              </span>
              <span className="community-stat-label">{t('templates.statLabel')}</span>
            </div>
          </div>
        </div>
      </section>

      <section className="community-templates" aria-labelledby="community-templates-title">
        <div className="community-templates-inner">
          <div className="community-section-head">
            <h2 id="community-templates-title" className="community-section-title">
              {t('templates.sectionTitle')}
            </h2>
            <div className="community-template-search">
              <label htmlFor="community-template-search-input" className="community-sr-only">
                {t('templates.searchLabel')}
              </label>
              <input
                id="community-template-search-input"
                type="search"
                className="community-template-search-input"
                placeholder={t('templates.searchPlaceholder')}
                value={templateSearchQuery}
                onChange={(e) => setTemplateSearchQuery(e.target.value)}
                autoComplete="off"
                enterKeyHint="search"
              />
            </div>
          </div>

          <div className="community-filters" role="toolbar" aria-label={t('templates.filterToolbar')}>
            <div className="community-filter-dropdown-wrap" ref={categoryFilterRef}>
              <button
                type="button"
                className={`community-filter-btn${categoryMenuOpen ? ' is-open' : ''}`}
                aria-expanded={categoryMenuOpen}
                aria-haspopup="listbox"
                aria-controls="community-category-dropdown"
                onClick={() => {
                  setSortMenuOpen(false);
                  setCategoryMenuOpen((open) => !open);
                }}
              >
                {selectedCategoryName ? catLabel(selectedCategoryName) : t('common.allCategories')}
                <HiOutlineChevronDown className="community-filter-chevron" aria-hidden />
              </button>
              {categoryMenuOpen && (
                <ul
                  id="community-category-dropdown"
                  className="community-filter-dropdown"
                  role="listbox"
                  aria-label={t('templates.categoryListbox')}
                >
                  <li className="community-filter-dropdown-item" role="none">
                    <button
                      type="button"
                      role="option"
                      className="community-filter-dropdown-btn"
                      aria-selected={selectedCategoryName === null}
                      onClick={() => {
                        setSelectedCategoryName(null);
                        setCategoryMenuOpen(false);
                      }}
                    >
                      <span>{t('common.allCategories')}</span>
                      <span className="community-filter-dropdown-count">
                        {t('common.countUnit', { n: categoryCounts.__all || 0 })}
                      </span>
                    </button>
                  </li>
                  {templateCategoriesLoading ? (
                    <li className="community-filter-dropdown-status" role="status">
                      {t('common.loadingCategories')}
                    </li>
                  ) : (
                    <>
                      {templateCategories.map((row, idx) => (
                        <li
                          key={`${row.name}-${idx}`}
                          className="community-filter-dropdown-item"
                          role="none"
                        >
                          <button
                            type="button"
                            role="option"
                            className="community-filter-dropdown-btn"
                            aria-selected={selectedCategoryName === row.name}
                            onClick={() => {
                              setSelectedCategoryName(row.name);
                              setCategoryMenuOpen(false);
                            }}
                          >
                            <span>{catLabel(row.name)}</span>
                            <span className="community-filter-dropdown-count">
                              {t('common.countUnit', { n: categoryCounts[row.name] || 0 })}
                            </span>
                          </button>
                        </li>
                      ))}
                      {templateCategories.length === 0 && (
                        <li className="community-filter-dropdown-status" role="status">
                          {t('common.noCategories')}
                        </li>
                      )}
                    </>
                  )}
                </ul>
              )}
            </div>
            <div className="community-filter-dropdown-wrap" ref={sortFilterRef}>
              <button
                type="button"
                className={`community-filter-btn${sortMenuOpen ? ' is-open' : ''}`}
                aria-expanded={sortMenuOpen}
                aria-haspopup="listbox"
                aria-controls="community-sort-dropdown"
                onClick={() => {
                  setCategoryMenuOpen(false);
                  setSortMenuOpen((open) => !open);
                }}
              >
                {t(`common.${templateSort}`)}
                <HiOutlineChevronDown className="community-filter-chevron" aria-hidden />
              </button>
              {sortMenuOpen && (
                <ul
                  id="community-sort-dropdown"
                  className="community-filter-dropdown"
                  role="listbox"
                  aria-label={t('templates.sortListbox')}
                >
                  {TEMPLATE_SORT_OPTIONS.map((opt) => (
                    <li key={opt.id} className="community-filter-dropdown-item" role="none">
                      <button
                        type="button"
                        role="option"
                        className="community-filter-dropdown-btn"
                        aria-selected={templateSort === opt.id}
                        onClick={() => {
                          setTemplateSort(opt.id);
                          setSortMenuOpen(false);
                        }}
                      >
                        {t(`common.${opt.id}`)}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {templateListLoading ? (
            <div
              className="community-templates-loading"
              role="status"
              aria-live="polite"
              aria-busy="true"
            >
              <div className="community-loading-spinner" aria-hidden />
            </div>
          ) : filteredTemplates.length === 0 ? (
            <p className="community-template-empty" role="status">
              {templateSearchQuery.trim()
                ? t('templates.emptySearch', { query: templateSearchQuery.trim() })
                : t('templates.empty')}
            </p>
          ) : selectedCategoryName ? (
            <div className="community-grid">
              {filteredTemplates.map((t) => renderTemplateCard(t))}
            </div>
          ) : (
            <div className="community-rows">
              {groupedTemplates.map((group) => (
                <section className="community-cat-row" key={group.category}>
                  <h3 className="community-cat-row-title">
                    {catLabel(group.category)}
                    {group.items.length > 0 && (
                      <span className="community-cat-row-count">
                        {t('common.countUnit', { n: group.items.length })}
                      </span>
                    )}
                  </h3>
                  {group.items.length === 0 ? (
                    <p className="community-cat-row-empty">{t('templates.catRowEmpty')}</p>
                  ) : (
                    <CatRowScroll>
                      {group.items.map((t) => renderTemplateCard(t, true))}
                    </CatRowScroll>
                  )}
                </section>
              ))}
            </div>
          )}
        </div>
      </section>

      {templateDescPreview && (
        <div
          className="community-modal-overlay"
          role="presentation"
          onClick={() => !templateImportLoading && setTemplateDescPreview(null)}
        >
          <div
            className="community-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="community-template-desc-title"
            aria-busy={templateImportLoading}
            onClick={(e) => e.stopPropagation()}
          >
            {templateImportLoading && (
              <div className="community-modal-loading-overlay" aria-live="polite" aria-busy="true">
                <div className="community-loading-spinner" aria-hidden />
                <p className="community-modal-loading-text">{t('templates.copying')}</p>
              </div>
            )}
            <div className="community-modal-header">
              <span className="community-modal-header-spacer" aria-hidden />
              <h2 id="community-template-desc-title" className="community-modal-title">
                {templateDescPreview.title}
              </h2>
              <button
                type="button"
                className="community-modal-close"
                aria-label={t('common.close')}
                disabled={templateImportLoading}
                onClick={() => setTemplateDescPreview(null)}
              >
                ×
              </button>
            </div>
            <p className="community-modal-section-label">{t('templates.descLabel')}</p>
            <div className="community-template-desc-scroll">
              <p className="community-template-desc-text">
                {templateDescPreview.desc ? templateDescPreview.desc : t('common.noDesc')}
              </p>
            </div>
            <p className="community-modal-section-label">{t('common.schedule')}</p>
            <p className="community-template-duration-line">
              {templateDescPreview.durationDays != null
                ? t('common.durationDays', { n: templateDescPreview.durationDays })
                : t('common.noSchedule')}
            </p>
            {templateDescPreview.durationDays != null && (
              <>
                <p className="community-modal-section-label community-modal-section-label--mt">
                  {t('common.workStartLabel')}
                </p>
                <div className="community-template-apply-date-wrap">
                  <label htmlFor="community-template-apply-start" className="community-sr-only">
                    {t('common.workStartLabel')}
                  </label>
                  <input
                    id="community-template-apply-start"
                    type="date"
                    className="community-template-apply-date"
                    value={templateApplyStartDate}
                    disabled={templateImportLoading}
                    onChange={(e) => setTemplateApplyStartDate(e.target.value)}
                  />
                </div>
              </>
            )}
            <div className="community-modal-actions">
              <button
                type="button"
                className="community-btn-outline community-modal-btn-secondary"
                disabled={templateImportLoading}
                onClick={() => setTemplateDescPreview(null)}
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                className="community-btn-primary community-modal-btn-primary"
                disabled={templateImportLoading}
                onClick={goToDashboardFromPreview}
              >
                {templateImportLoading ? t('common.importing') : t('common.use')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default TemplatesTab;
